<template>
    <section class="layout">
        <div class="main-grid-container">
            <h3 class="profile-label grid-item" data-qa="money-heading">
                Money
            </h3>

            <div class="input-row">
                <ds-dropdown class="dropdown" position="bottom-end" close-on-menu-click>
                    <ds-filled-button data-qa="add-money-button" button-class="primary" trailing-icon="chevron-down">
                        Add
                    </ds-filled-button>

                    <div slot="menu">
                        <ul class="money-dropdown-menu-list">
                            <li
                                v-for="(temp, index) in templateObject"
                                :key="index"
                                class="dropdown-menu-item"
                                @click="saveFromTemplate(temp.label)"
                            >
                                {{ temp.label }}
                            </li>
                        </ul>
                    </div>
                </ds-dropdown>
            </div>
        </div>

        <div class="line-straight-up" />

        <div
            v-if="isContactMoneyHasNoSubTabs"
            class="no-sub-tabs-main"
            data-qa="no-subTabs"
        >
            <span
                class="no-all-sub-tabs"
            >
                No credit cards, quotes, orders, payments or subscriptions.
            </span>
        </div>

        <div v-else>
            <!--This is for Credit Cards Tab-->
            <div
                v-if="isThisTabHasData(creditCards.length)"
                data-qa="credit-card-tab"
            >
                <contact-money-credit-cards
                    :credit-cards="creditCards"
                    :contact="contact"
                />
            </div>

            <!--This is for Quotes Tab-->
            <div
                v-if="isThisTabHasData(quotes.length)"
                data-qa="quotes-tab"
            >
                <contact-money-quotes :quotes="quotes" />
            </div>

            <!--This is for Orders Tab-->
            <div
                v-if="isThisTabHasData(orders.length)"
                data-qa="orders-tab"
            >
                <contact-money-orders
                    :orders="orders"
                    :orders-count="ordersCount"
                    :contact="contact"
                />
            </div>

            <!--This is for Payments Tab-->
            <div
                v-if="isThisTabHasData(payments.length)"
                data-qa="payments-tab"
            >
                <contact-money-payments
                    :payments="payments"
                />
            </div>

            <!--This is for Subscriptions Tab-->
            <div
                v-if="isThisTabHasData(subscriptions.length)"
                data-qa="subscriptions-tab"
            >
                <contact-money-subscriptions
                    :subscriptions="subscriptions"
                    :contact="contact"
                />
            </div>

            <!--This is for Recurring charges Tab-->
            <div
                v-if="isThisTabHasData(recurringCharges.length)"
                data-qa="recurring-tab"
            >
                <contact-money-recurring-charges
                    :recurring-charges="recurringCharges"
                />
            </div>
        </div>
        <credit-card-form ref="creditCardModal" :contact="contact" />

        <div class="line-straight-down" />
    </section>
</template>

<script>
import axios from 'axios';
import ContactMoneyQuotes from './ContactMoneyQuotes';
import ContactMoneyCreditCards from './ContactMoneyCreditCards';
import ContactMoneyOrders from './ContactMoneyOrders';
import ContactMoneyPayments from './ContactMoneyPayments';
import ContactMoneySubscriptions from './ContactMoneySubscriptions';
import ContactMoneyRecurringCharges from './ContactMoneyRecurringCharges';
import CreditCardForm from '@/components/contact/CreditCardForm';

export default {
    components: {
        ContactMoneyQuotes,
        ContactMoneyCreditCards,
        ContactMoneyOrders,
        ContactMoneyPayments,
        ContactMoneySubscriptions,
        ContactMoneyRecurringCharges,
        CreditCardForm,
    },

    props: {
        contact: Object,
    },

    data() {
        return {
            url: '/app/ecommContactDisplay',
            creditCards: [],
            quotes: [],
            orders: [],
            payments: [],
            subscriptions: [],
            recurringCharges: [],
            ordersCount: 0,
            ordersLimit: 10,
            contactId: this.contact.id,
            contactOwnerType: 'CONTACT',
            loaded: false,
            templateObject: [],
        };
    },

    created() {
        if (!this.loaded) {
            this.initTab();
            this.loaded = true;
        }
    },

    mounted() {
        this.$root.$on('RELOAD_CREDIT_CARD_DETAILS', (status) => {
            if (status) {
                this.loadContactMoneyCreditCard();
            }
        });
    },

    beforeDestroy() {
        this.$root.$off('RELOAD_CREDIT_CARD_DETAILS');
    },

    computed: {
        isContactMoneyHasNoSubTabs() {
            if (
                this.creditCards?.length === 0
                && this.quotes?.length === 0
                && this.orders?.length === 0
                && this.payments?.length === 0
                && this.subscriptions?.length === 0
                && this.recurringCharges?.length === 0
            ) {
                return true;
            }

            return false;
        },
    },

    methods: {
        async loadContactMoneyCreditCard() {
            try {
                const response = await
                axios.get(`${this.url}/${this.contactId}/creditCards/${this.contactOwnerType}`);

                this.creditCards = response.data;
            } catch (error) {
                this.$error({ message: 'unable to get credit card details' });
            }
        },

        async loadContactMoneyQuotes() {
            try {
                const response = await
                axios.get(`${this.url}/${this.contactId}/recentQuotes`);

                this.quotes = response.data;
            } catch (error) {
                this.$error({ message: 'unable to get Quotes details' });
            }
        },

        async loadContactMoneyOrders() {
            try {
                const response = await
                axios.get(`${this.url}/${this.contactId}/recentOrders/${this.ordersLimit}/${this.contactOwnerType}`);

                this.orders = response.data;
            } catch (error) {
                this.$error({ message: 'unable to get money details' });
            }
        },

        async loadContactMoneyOrdersCount() {
            try {
                const response = await
                axios.get(`${this.url}/${this.contactId}/ordersCount`);

                this.ordersCount = response.data;
            } catch (error) {
                this.$error({ message: 'unable to get orders count details' });
            }
        },

        async loadContactMoneyPayments() {
            try {
                const response = await
                axios.get(`${this.url}/${this.contactId}/recentPayments`);

                this.payments = response.data;
            } catch (error) {
                this.$error({ message: 'unable to get payments details' });
            }
        },

        async loadContactMoneySubscriptions() {
            try {
                const response = await
                axios.get(`${this.url}/${this.contactId}/recentJobRecurringRecords/${this.contactOwnerType}`);

                this.subscriptions = response.data;
            } catch (error) {
                this.$error({ message: 'unable to get Subscriptions details' });
            }
        },

        async loadContactMoneyRecurringCharges() {
            try {
                const response = await
                axios.get(`${this.url}/${this.contactId}/recentRecurringCharges/${this.contactOwnerType}`);

                this.recurringCharges = response.data;
            } catch (error) {
                this.$error({ message: 'unable to get Recurring Charges details' });
            }
        },

        initTab() {
            this.loadContactMoneyCreditCard();
            this.loadContactMoneyQuotes();
            this.loadContactMoneyOrders();
            this.loadContactMoneyPayments();
            this.loadContactMoneySubscriptions();
            this.loadContactMoneyRecurringCharges();
            this.loadContactMoneyOrdersCount();
            this.loadMoneyDropDowns();
        },

        isThisTabHasData(length) {
            return length > 0;
        },

        async loadMoneyDropDowns() {
            try {
                const response = await axios.get(`${this.url}/loadDropDownForMoney`);

                const templateDrop = response.data.templateList;

                delete templateDrop.quote;

                this.templateObject = Object.keys(templateDrop).map((key) => {
                    return { label: templateDrop[key], value: Number(key) };
                });
            } catch (error) {
                this.$error({ message: 'error occurred while getting drop downs' });
            }
        },

        saveFromTemplate(tempValue) {
            if (tempValue !== '' && tempValue !== null) {
                switch (tempValue) {
                case 'Credit card':
                    this.$refs.creditCardModal.showModal(0);
                    break;
                case 'Order':
                    window.open(`/Job/manageJob.jsp?Order0ContactId=${this.contactId}&view=add`);
                    break;
                case 'Payments':
                    window.open(`/app/payment/prepareAddPaymentForContact?contactId=${this.contactId}`);
                    break;
                case 'Subscription':
                    window.open(`/JobRecurring/manageJobRecurring.jsp?view=add&JobRecurring0ContactId=${this.contactId}`);
                    break;
                default:
                    break;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.line-straight-up {
    border-bottom: 1px solid $color-gray-lighter;
    display: flex;
}

.grid-item {
    margin: auto;
}

.profile-label {
    margin-left: 0;
}

.button-dropdown {
    --dropdown-minwidth: 10.5rem;
}

.no-sub-tabs-main {
    text-align: center;
    margin-top: 5rem;
    margin-bottom: 4.5rem;
}

.no-all-sub-tabs {
    font-size: $font-size-lg;
    color: $color-text;
}

.main-grid-container {
    display: flex;
    margin: $spacing-250 0;
}

.input-row {
    width : auto;
}

.money-dropdown-menu-list {
    padding: 0.01rem 0;
}

.chevron {
    --icon-size: 1rem;
    margin-bottom: - $spacing-050;
}

@media only screen and ($medium) {
    .main-grid-container {
        flex-direction: row;
    }
}
</style>
